import { motion, AnimatePresence } from 'framer-motion';
import { useToastStore } from '../../stores';
import { X, CheckCircle, XCircle, Warning, Info } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

const variantStyles = {
  success: {
    bg: 'bg-nexus-accent-mint/10 border-nexus-accent-mint/30',
    icon: CheckCircle,
    iconColor: 'text-nexus-accent-mint',
    progressColor: 'bg-nexus-accent-mint',
  },
  error: {
    bg: 'bg-nexus-error/10 border-nexus-error/30',
    icon: XCircle,
    iconColor: 'text-nexus-error',
    progressColor: 'bg-nexus-error',
  },
  warning: {
    bg: 'bg-nexus-accent-amber/10 border-nexus-accent-amber/30',
    icon: Warning,
    iconColor: 'text-nexus-accent-amber',
    progressColor: 'bg-nexus-accent-amber',
  },
  info: {
    bg: 'bg-nexus-accent-violet/10 border-nexus-accent-violet/30',
    icon: Info,
    iconColor: 'text-nexus-accent-violet',
    progressColor: 'bg-nexus-accent-violet',
  },
};

const ToastItem = ({ toast, onRemove }) => {
  const [progress, setProgress] = useState(100);
  const duration = toast.duration || 5000;
  const style = variantStyles[toast.variant] || variantStyles.info;
  const Icon = style.icon;
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - (100 / (duration / 100));
        return newProgress < 0 ? 0 : newProgress;
      });
    }, 100);
    
    return () => clearInterval(interval);
  }, [duration]);
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 50, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 50, scale: 0.9 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`
        relative overflow-hidden
        w-80 p-4 rounded-lg border backdrop-blur-xl
        ${style.bg}
      `}
    >
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 flex-shrink-0 ${style.iconColor}`} weight="duotone" />
        <div className="flex-1 min-w-0">
          {toast.title && (
            <p className="text-sm font-semibold text-nexus-text-primary">{toast.title}</p>
          )}
          <p className="text-sm text-nexus-text-secondary">{toast.message}</p>
        </div>
        <button 
          onClick={() => onRemove(toast.id)}
          className="text-nexus-text-tertiary hover:text-nexus-text-primary transition-colors"
        >
          <X className="w-4 h-4" weight="bold" />
        </button>
      </div>
      
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
        <motion.div 
          className={`h-full ${style.progressColor}`}
          style={{ width: `${progress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>
    </motion.div>
  );
};

export const Toaster = () => {
  const { toasts, removeToast } = useToastStore();
  
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </AnimatePresence>
    </div>
  );
};

export const NexusToast = Toaster;
